import { ImagePlus, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import api from "../../../configs/api";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
import { ModalWrapper } from "../common/ModalWrapper";
import toast from "react-hot-toast";

const EditBlogModal = ({ modal, onBlogupdated }) => {
  const { isOpen, mode, data: blogId, close } = modal;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    description: "",
    images: [],
  });

  const [uploads, setUploads] = useState([]);

  // useEffect(() => {
  //   console.log("uploads:", uploads);
  // }, [uploads]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchBlog = async () => {
      if (mode !== "edit" || !blogId) return;

      try {
        setLoading(true);

        const res = await api.get(`/blogs/${blogId}`);
        const blog = res.data?.data;

        setForm({
          name: blog.name || "",
          description: blog.description || "",

          images: blog.images || [],
        });

        const uploads = blog?.images?.map((img) => ({
          blobUrl: null,
          progress: 0,
          uploaded: true,
          url: img,
        }));

        setUploads(uploads);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (mode === "create") {
      setForm({
        name: "",
        description: "",

        images: [],
      });
      setUploads([]);
    }

    fetchBlog();
  }, [isOpen, mode, blogId]);

  /*  FIELD UPDATE  */
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /*  IMAGE PICKER  */
  const handleImageSelect = (files) => {
    const fileList = Array.from(files);

    fileList.forEach((file) => {
      const blobUrl = URL.createObjectURL(file);

      const uploadItem = {
        blobUrl,
        progress: 0,
        uploaded: false,
        url: null,
      };

      setUploads((prev) => [...prev, uploadItem]);

      const { uploadPromise } = uploadFileToCloudinary(file, (percent) => {
        setUploads((prev) =>
          prev.map((u) =>
            u.blobUrl === blobUrl ? { ...u, progress: percent } : u,
          ),
        );
      });

      uploadPromise.then((res) => {
        if (!res) return;

        setUploads((prev) =>
          prev.map((u) =>
            u.blobUrl === blobUrl
              ? { ...u, uploaded: true, url: res.url, progress: 100 }
              : u,
          ),
        );

        setForm((prev) => ({
          ...prev,
          images: [...prev.images, res.url],
        }));
      });
    });
  };

  const removeImage = (index) => {
    const updated = form.images.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      images: updated,
    }));

    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  /*  SUBMIT  */
  const handleSubmit = async () => {
    try {
      setSaving(true);
      let res;

      if (mode === "create") {
        res = await api.post("/blogs", form);
        toast.success("Blog created successfully");
      } else {
        res = await api.patch(`/blogs/${blogId}`, form);
        toast.success("Blog updated successfully");
      }

      const blog = res.data?.data;

      if (mode === "edit" && onBlogupdated) {
        onBlogupdated(blog);
      }

      close();
    } catch (err) {
      toast.error("Blog action failed");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  /*  IMAGE GRID  */

  const renderImages = () => {
    if (!uploads.length) return null;

    return (
      <div className="grid grid-cols-4 gap-3 mt-3">
        {uploads.map((img, index) => (
          <div
            key={index}
            className="relative aspect-square border border-borderLight overflow-hidden"
          >
            <img
              alt="img"
              src={img.uploaded ? img.url : img.blobUrl}
              className="w-full h-full object-cover"
            />

            {!img.uploaded && (
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white text-xs">
                <Loader2 className="animate-spin mb-1" size={16} />
                {img.progress}%
              </div>
            )}

            <button
              onClick={() => removeImage(index)}
              className="absolute top-1 right-1 bg-black/70 hover:text-red-500 hover:bg-red-200 transition-all duration-50 cursor-pointer text-white p-1"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  /*  UI  */

  return (
    <ModalWrapper
      title={mode === "create" ? "Create Blog" : "Edit Blog"}
      isOpen={isOpen}
      onClose={close}
      primaryActionLabel={mode === "create" ? "Create Blog" : "Save Changes"}
      primaryLoading={saving}
      allowEasyClose={false}
      onPrimaryAction={handleSubmit}
      maxWidth="max-w-3xl"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-5 text-sm">
          {/* NAME */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Blog Name
            </label>

            <input
              value={form.name}
              placeholder="e.g. Tata Memorial Center"
              onChange={(e) => updateField("name", e.target.value)}
              className="mt-1 h-[44px] w-full text-sm px-4 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Description
            </label>

            <textarea
              rows={4}
              placeholder="e.g. A Grant – in – Aid Institution under Department of Atomic Energy Government of India."
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              className="mt-1 w-full px-4 py-3 border border-borderLight outline-none focus:border-primaryAccent"
            />
          </div>

          {/* IMAGE UPLOADER */}
          <div>
            <label className="text-sm font-medium text-headingText">
              Blog Images
            </label>

            <label
              className="
              mt-2 flex flex-col items-center justify-center
              border-2 border-dashed border-borderLight
              h-[140px] cursor-pointer hover:border-primaryAccent
              transition"
            >
              <ImagePlus size={28} className="text-descriptionText mb-2" />
              <span className="text-xs text-gray-400 text-center ">
                Upload blog images (JPG / PNG) <br /> Example: blog texture,
                construction site, finished pavement{" "}
              </span>

              <input
                type="file"
                multiple
                hidden
                accept="image/*"
                onChange={(e) => handleImageSelect(e.target.files)}
              />
            </label>

            {renderImages()}
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default EditBlogModal;
